import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AcceptGroupInvitationParams,
  AcceptOrganizationInvitationParams,
  ChangeOrganizationOwnerParams,
  CreateGroupParams,
  CreateGroupResponse,
  CreateOrganizationParams,
  CreateOrganizationResponse,
  DeleteGroupInvitationParams,
  DeleteGroupParams,
  DeleteOrganizationInvitationParams,
  DeleteOrganizationParams,
  GetGroupInvitationParams,
  GetGroupInvitationResponse,
  GetGroupInvitationsParams,
  GetGroupInvitationsResponse,
  GetGroupParams,
  GetGroupResponse,
  GetGroupsParams,
  GetGroupsResponse,
  GetGroupUsersParams,
  GetGroupUsersResponse,
  GetOrganizationInvitationParams,
  GetOrganizationInvitationResponse,
  GetOrganizationInvitationsParams,
  GetOrganizationInvitationsResponse,
  GetOrganizationParams,
  GetOrganizationResponse,
  GetOrganizationsParams,
  GetOrganizationsResponse,
  GetOrganizationUsersParams,
  GetOrganizationUsersResponse,
  Group,
  Invitation,
  InviteUserToGroupParams,
  InviteUserToGroupResponse,
  InviteUserToOrganizationParams,
  InviteUserToOrganizationResponse,
  LeaveGroupParams,
  LeaveOrganizationParams,
  Organization,
  RegisterNewUserParams,
  RegisterNewUserResponse,
  RemoveUserFromGroupParams,
  RemoveUserFromOrganizationParams,
  SubmitHubSpotFormParams,
  UpdateBillingAdminsListParams,
  UpdateGroupParams,
  UpdateGroupResponse,
  UpdateOrganizationParams,
  UpdateOrganizationResponse,
  User
} from "./types";

export type EnterprisesState = {
  organization: Organization | null;
  organizations: Organization[] | null;
  organizationInvitations: Invitation[] | null;
  organizationInvitation: Invitation | null;
  areOrganizationInvitationsLoading: boolean;
  isOrganizationInvitationLoading: boolean;
  isUserInvitingToOrganization: boolean;
  isOrganizationInvitationAccepting: boolean;
  isOrganizationInvitationDeleting: boolean;
  organizationInvitationAcceptanceError: string | null;
  isOrganizationLoading: boolean;
  areOrganizationsLoading: boolean;
  isOrganizationCreating: boolean;
  isOrganizationUpdating: boolean;
  isOrganizationDeleting: boolean;
  isOrganizationOwnerChanging: boolean;
  group: Group | null;
  groups: Group[] | null;
  groupInvitations: Invitation[] | null;
  areGroupInvitationsLoading: boolean;
  groupInvitation: Invitation | null;
  isGroupInvitationLoading: boolean;
  isUserInvitingToGroup: boolean;
  isGroupInvitationAccepting: boolean;
  isGroupInvitationDeleting: boolean;
  groupInvitationAcceptanceError: string | null;
  isGroupLoading: boolean;
  areGroupsLoading: boolean;
  isGroupCreating: boolean;
  isGroupUpdating: boolean;
  isGroupDeleting: boolean;
  isGroupLeaving: boolean;
  areGroupUsersLoading: boolean;
  areOrganizationUsersLoading: boolean;
  isUserRemovingFromGroup: boolean;
  groupUsers: User[] | null;
  organizationUsers: User[] | null;
  isUserRemovingFromOrganization: boolean;
  userIdToRemoveFromOrganization: string | null;
  userRemovalFromOrganizationError: string | null;
  isHubspotFormSubmitting: boolean;
  isNewUserRegistering: boolean;
  isBillingAdminsListUpdating: boolean;
};

const initialState: EnterprisesState = {
  organization: null,
  organizations: null,
  organizationInvitations: null,
  organizationInvitation: null,
  areOrganizationInvitationsLoading: false,
  isOrganizationInvitationLoading: false,
  isUserInvitingToOrganization: false,
  isOrganizationInvitationAccepting: false,
  isOrganizationInvitationDeleting: false,
  organizationInvitationAcceptanceError: null,
  isOrganizationLoading: false,
  areOrganizationsLoading: false,
  isOrganizationCreating: false,
  isOrganizationUpdating: false,
  isOrganizationDeleting: false,
  isOrganizationOwnerChanging: false,
  group: null,
  groups: null,
  groupInvitations: null,
  areGroupInvitationsLoading: false,
  groupInvitation: null,
  isGroupInvitationLoading: false,
  isUserInvitingToGroup: false,
  isGroupInvitationAccepting: false,
  isGroupInvitationDeleting: false,
  groupInvitationAcceptanceError: null,
  isGroupLoading: false,
  areGroupsLoading: false,
  isGroupCreating: false,
  isGroupUpdating: false,
  isGroupDeleting: false,
  isGroupLeaving: false,
  areGroupUsersLoading: false,
  areOrganizationUsersLoading: false,
  isUserRemovingFromGroup: false,
  isUserRemovingFromOrganization: false,
  userIdToRemoveFromOrganization: null,
  userRemovalFromOrganizationError: null,
  groupUsers: null,
  organizationUsers: null,
  isHubspotFormSubmitting: false,
  isNewUserRegistering: false,
  isBillingAdminsListUpdating: false
};

const enterprisesSlice = createSlice({
  name: "enterprises",
  initialState,
  reducers: {
    // Get Organization
    getOrganizationRequest: (
      state,
      action: PayloadAction<GetOrganizationParams>
    ) => {
      state.isOrganizationLoading = true;
    },
    getOrganizationSuccess: (
      state,
      action: PayloadAction<GetOrganizationResponse>
    ) => {
      state.organization = action.payload;
      state.isOrganizationLoading = false;
    },
    getOrganizationFailure: (
      state,
      action: PayloadAction<{ params: GetOrganizationParams; error: string }>
    ) => {
      state.isOrganizationLoading = false;
    },

    // Get Organizations
    getOrganizationsRequest: (
      state,
      action: PayloadAction<GetOrganizationsParams>
    ) => {
      state.areOrganizationsLoading = true;
    },
    getOrganizationsSuccess: (
      state,
      action: PayloadAction<GetOrganizationsResponse>
    ) => {
      state.organizations = action.payload;
      state.areOrganizationsLoading = false;
    },
    getOrganizationsFailure: (
      state,
      action: PayloadAction<{ params: GetOrganizationsParams; error: string }>
    ) => {
      state.areOrganizationsLoading = false;
    },

    // Create Organization
    createOrganizationRequest: (
      state,
      action: PayloadAction<CreateOrganizationParams>
    ) => {
      state.isOrganizationCreating = true;
    },
    createOrganizationSuccess: (
      state,
      action: PayloadAction<CreateOrganizationResponse>
    ) => {
      state.isOrganizationCreating = false;
    },
    createOrganizationFailure: (
      state,
      action: PayloadAction<{ params: CreateOrganizationParams; error: string }>
    ) => {
      state.isOrganizationCreating = false;
    },

    // Update Organization
    updateOrganizationRequest: (
      state,
      action: PayloadAction<UpdateOrganizationParams>
    ) => {
      state.isOrganizationUpdating = true;
    },
    updateOrganizationSuccess: (
      state,
      action: PayloadAction<UpdateOrganizationResponse>
    ) => {
      state.isOrganizationUpdating = false;
    },
    updateOrganizationFailure: (
      state,
      action: PayloadAction<{ params: UpdateOrganizationParams; error: string }>
    ) => {
      state.isOrganizationUpdating = false;
    },

    // Delete Organization
    deleteOrganizationRequest: (
      state,
      action: PayloadAction<DeleteOrganizationParams>
    ) => {
      state.isOrganizationDeleting = true;
    },
    deleteOrganizationSuccess: (state) => {
      state.isOrganizationDeleting = false;
      state.organization = null;
    },
    deleteOrganizationFailure: (
      state,
      action: PayloadAction<{ params: DeleteOrganizationParams; error: string }>
    ) => {
      state.isOrganizationDeleting = false;
    },

    // Leave Organization
    leaveOrganizationRequest: (
      state,
      action: PayloadAction<LeaveOrganizationParams>
    ) => {},
    leaveOrganizationSuccess: (state) => {},
    leaveOrganizationFailure: (
      state,
      action: PayloadAction<{ params: LeaveOrganizationParams; error: string }>
    ) => {},

    // Change Organization Owner
    changeOrganizationOwnerRequest: (
      state,
      action: PayloadAction<ChangeOrganizationOwnerParams>
    ) => {
      state.isOrganizationOwnerChanging = true;
    },
    changeOrganizationOwnerSuccess: (state) => {
      state.isOrganizationOwnerChanging = false;
    },
    changeOrganizationOwnerFailure: (
      state,
      action: PayloadAction<{
        params: ChangeOrganizationOwnerParams;
        error: string;
      }>
    ) => {
      state.isOrganizationOwnerChanging = false;
    },

    // Get Group
    getGroupRequest: (state, action: PayloadAction<GetGroupParams>) => {
      state.isGroupLoading = true;
    },
    getGroupSuccess: (state, action: PayloadAction<GetGroupResponse>) => {
      state.group = action.payload;
      state.isGroupLoading = false;
    },
    getGroupFailure: (
      state,
      action: PayloadAction<{ params: GetGroupParams; error: string }>
    ) => {
      state.isGroupLoading = false;
    },

    // Get Groups
    getGroupsRequest: (state, action: PayloadAction<GetGroupsParams>) => {
      state.areGroupsLoading = true;
    },
    getGroupsSuccess: (state, action: PayloadAction<GetGroupsResponse>) => {
      state.groups = action.payload;
      state.areGroupsLoading = false;
    },
    getGroupsFailure: (
      state,
      action: PayloadAction<{ params: GetGroupsParams; error: string }>
    ) => {
      state.areGroupsLoading = false;
    },

    // Create Group
    createGroupRequest: (state, action: PayloadAction<CreateGroupParams>) => {
      state.isGroupCreating = true;
    },
    createGroupSuccess: (state, action: PayloadAction<CreateGroupResponse>) => {
      state.isGroupCreating = false;
    },
    createGroupFailure: (
      state,
      action: PayloadAction<{ params: CreateGroupParams; error: string }>
    ) => {
      state.isGroupCreating = false;
    },

    // Update Group
    updateGroupRequest: (state, action: PayloadAction<UpdateGroupParams>) => {
      state.isGroupUpdating = true;
    },
    updateGroupSuccess: (state, action: PayloadAction<UpdateGroupResponse>) => {
      state.isGroupUpdating = false;
    },
    updateGroupFailure: (
      state,
      action: PayloadAction<{ params: UpdateGroupParams; error: string }>
    ) => {
      state.isGroupUpdating = false;
    },

    // Delete Group
    deleteGroupRequest: (state, action: PayloadAction<DeleteGroupParams>) => {
      state.isGroupDeleting = true;
    },
    deleteGroupSuccess: (state) => {
      state.isGroupDeleting = false;
      state.group = null;
    },
    deleteGroupFailure: (
      state,
      action: PayloadAction<{ params: DeleteGroupParams; error: string }>
    ) => {
      state.isGroupDeleting = false;
    },

    // Leave Group
    leaveGroupRequest: (state, action: PayloadAction<LeaveGroupParams>) => {
      state.isGroupLeaving = true;
    },
    leaveGroupSuccess: (state) => {
      state.isGroupLeaving = false;
      state.group = null;
    },
    leaveGroupFailure: (
      state,
      action: PayloadAction<{ params: LeaveGroupParams; error: string }>
    ) => {
      state.isGroupLeaving = false;
    },

    // Get Organization Users
    getOrganizationUsersRequest: (
      state,
      action: PayloadAction<GetOrganizationUsersParams>
    ) => {
      state.areOrganizationUsersLoading = true;
    },
    getOrganizationUsersSuccess: (
      state,
      action: PayloadAction<GetOrganizationUsersResponse>
    ) => {
      state.organizationUsers = action.payload;
      state.areOrganizationUsersLoading = false;
    },
    getOrganizationUsersFailure: (
      state,
      action: PayloadAction<{
        params: GetOrganizationUsersParams;
        error: string;
      }>
    ) => {
      state.areOrganizationUsersLoading = false;
    },

    // Get Group Users
    getGroupUsersRequest: (
      state,
      action: PayloadAction<GetGroupUsersParams>
    ) => {
      state.areGroupUsersLoading = true;
    },
    getGroupUsersSuccess: (
      state,
      action: PayloadAction<GetGroupUsersResponse>
    ) => {
      state.groupUsers = action.payload;
      state.areGroupUsersLoading = false;
    },
    getGroupUsersFailure: (
      state,
      action: PayloadAction<{ params: GetGroupUsersParams; error: string }>
    ) => {
      state.areGroupUsersLoading = false;
    },

    // Remove User From Organization
    removeUserFromOrganizationRequest: (
      state,
      action: PayloadAction<RemoveUserFromOrganizationParams>
    ) => {
      state.isUserRemovingFromOrganization = true;
      state.userIdToRemoveFromOrganization = action.payload.userId;
      state.userRemovalFromOrganizationError = null;
    },
    removeUserFromOrganizationSuccess: (state) => {
      state.isUserRemovingFromOrganization = false;
    },
    removeUserFromOrganizationFailure: (
      state,
      action: PayloadAction<{
        params: RemoveUserFromOrganizationParams;
        error: string;
      }>
    ) => {
      state.isUserRemovingFromOrganization = false;
      state.userRemovalFromOrganizationError = action.payload.error;
    },

    // Remove User From Group
    removeUserFromGroupRequest: (
      state,
      action: PayloadAction<RemoveUserFromGroupParams>
    ) => {
      state.isUserRemovingFromGroup = true;
    },
    removeUserFromGroupSuccess: (state) => {
      state.isUserRemovingFromGroup = false;
    },
    removeUserFromGroupFailure: (
      state,
      action: PayloadAction<{
        params: RemoveUserFromGroupParams;
        error: string;
      }>
    ) => {
      state.isUserRemovingFromGroup = false;
    },

    // Submit HubSpot Form
    submitHubSpotFormRequest: (
      state,
      action: PayloadAction<SubmitHubSpotFormParams>
    ) => {
      state.isHubspotFormSubmitting = true;
    },
    submitHubSpotFormSuccess: (state) => {
      state.isHubspotFormSubmitting = false;
    },
    submitHubSpotFormFailure: (
      state,
      action: PayloadAction<{ params: SubmitHubSpotFormParams; error: string }>
    ) => {
      state.isHubspotFormSubmitting = false;
    },

    // Get Organization Invitations
    getOrganizationInvitationsRequest: (
      state,
      action: PayloadAction<GetOrganizationInvitationsParams>
    ) => {
      state.areOrganizationInvitationsLoading = true;
    },
    getOrganizationInvitationsSuccess: (
      state,
      action: PayloadAction<GetOrganizationInvitationsResponse>
    ) => {
      state.organizationInvitations = action.payload;
      state.areOrganizationInvitationsLoading = false;
    },
    getOrganizationInvitationsFailure: (
      state,
      action: PayloadAction<{
        params: GetOrganizationInvitationsParams;
        error: string;
      }>
    ) => {
      state.areOrganizationInvitationsLoading = false;
    },

    // Get Organization Invitation
    getOrganizationInvitationRequest: (
      state,
      action: PayloadAction<GetOrganizationInvitationParams>
    ) => {
      state.isOrganizationInvitationLoading = true;
    },
    getOrganizationInvitationSuccess: (
      state,
      action: PayloadAction<GetOrganizationInvitationResponse>
    ) => {
      state.organizationInvitation = action.payload;
      state.isOrganizationInvitationLoading = false;
    },
    getOrganizationInvitationFailure: (
      state,
      action: PayloadAction<{
        params: GetOrganizationInvitationParams;
        error: string;
      }>
    ) => {
      state.isOrganizationInvitationLoading = false;
    },

    // Invite User To Organization
    inviteUserToOrganizationRequest: (
      state,
      action: PayloadAction<InviteUserToOrganizationParams>
    ) => {
      state.isUserInvitingToOrganization = true;
    },
    inviteUserToOrganizationSuccess: (
      state,
      action: PayloadAction<InviteUserToOrganizationResponse>
    ) => {
      state.isUserInvitingToOrganization = false;
    },
    inviteUserToOrganizationFailure: (
      state,
      action: PayloadAction<{
        params: InviteUserToOrganizationParams;
        error: string;
      }>
    ) => {
      state.isUserInvitingToOrganization = false;
    },

    // Accept Organization Invitation
    acceptOrganizationInvitationRequest: (
      state,
      action: PayloadAction<AcceptOrganizationInvitationParams>
    ) => {
      state.isOrganizationInvitationAccepting = true;
    },
    acceptOrganizationInvitationSuccess: (state) => {
      state.isOrganizationInvitationAccepting = false;
    },
    acceptOrganizationInvitationFailure: (
      state,
      action: PayloadAction<{
        params: AcceptOrganizationInvitationParams;
        error: string;
      }>
    ) => {
      state.isOrganizationInvitationAccepting = false;
      state.organizationInvitationAcceptanceError = action.payload.error;
    },

    // Delete Organization Invitation
    deleteOrganizationInvitationRequest: (
      state,
      action: PayloadAction<DeleteOrganizationInvitationParams>
    ) => {
      state.isOrganizationInvitationDeleting = true;
    },
    deleteOrganizationInvitationSuccess: (state) => {
      state.isOrganizationInvitationDeleting = false;
    },
    deleteOrganizationInvitationFailure: (
      state,
      action: PayloadAction<{
        params: DeleteOrganizationInvitationParams;
        error: string;
      }>
    ) => {
      state.isOrganizationInvitationDeleting = false;
    },

    // Get Group Invitations
    getGroupInvitationsRequest: (
      state,
      action: PayloadAction<GetGroupInvitationsParams>
    ) => {
      state.areGroupInvitationsLoading = true;
    },
    getGroupInvitationsSuccess: (
      state,
      action: PayloadAction<GetGroupInvitationsResponse>
    ) => {
      state.groupInvitations = action.payload;
      state.areGroupInvitationsLoading = false;
    },
    getGroupInvitationsFailure: (
      state,
      action: PayloadAction<{
        params: GetGroupInvitationsParams;
        error: string;
      }>
    ) => {
      state.areGroupInvitationsLoading = false;
    },

    // Get Group Invitation
    getGroupInvitationRequest: (
      state,
      action: PayloadAction<GetGroupInvitationParams>
    ) => {
      state.isGroupInvitationLoading = true;
    },
    getGroupInvitationSuccess: (
      state,
      action: PayloadAction<GetGroupInvitationResponse>
    ) => {
      state.groupInvitation = action.payload;
      state.isGroupInvitationLoading = false;
    },
    getGroupInvitationFailure: (
      state,
      action: PayloadAction<{ params: GetGroupInvitationParams; error: string }>
    ) => {
      state.isGroupInvitationLoading = false;
    },

    // Invite User To Group
    inviteUserToGroupRequest: (
      state,
      action: PayloadAction<InviteUserToGroupParams>
    ) => {
      state.isUserInvitingToGroup = true;
    },
    inviteUserToGroupSuccess: (
      state,
      action: PayloadAction<InviteUserToGroupResponse>
    ) => {
      state.isUserInvitingToGroup = false;
    },
    inviteUserToGroupFailure: (
      state,
      action: PayloadAction<{ params: InviteUserToGroupParams; error: string }>
    ) => {
      state.isUserInvitingToGroup = false;
    },

    // Accept Group Invitation
    acceptGroupInvitationRequest: (
      state,
      action: PayloadAction<AcceptGroupInvitationParams>
    ) => {
      state.isGroupInvitationAccepting = true;
    },
    acceptGroupInvitationSuccess: (state) => {
      state.isGroupInvitationAccepting = false;
    },
    acceptGroupInvitationFailure: (
      state,
      action: PayloadAction<{
        params: AcceptGroupInvitationParams;
        error: string;
      }>
    ) => {
      state.isGroupInvitationAccepting = false;
      state.groupInvitationAcceptanceError = action.payload.error;
    },

    // Delete Group Invitation
    deleteGroupInvitationRequest: (
      state,
      action: PayloadAction<DeleteGroupInvitationParams>
    ) => {
      state.isGroupInvitationDeleting = true;
    },
    deleteGroupInvitationSuccess: (state) => {
      state.isGroupInvitationDeleting = false;
    },
    deleteGroupInvitationFailure: (
      state,
      action: PayloadAction<{
        params: DeleteGroupInvitationParams;
        error: string;
      }>
    ) => {
      state.isGroupInvitationDeleting = false;
    },

    // Register New User
    registerNewUserRequest: (
      state,
      action: PayloadAction<RegisterNewUserParams>
    ) => {
      state.isNewUserRegistering = true;
    },
    registerNewUserSuccess: (
      state,
      action: PayloadAction<RegisterNewUserResponse>
    ) => {
      state.isNewUserRegistering = false;
    },
    registerNewUserFailure: (
      state,
      action: PayloadAction<{ params: RegisterNewUserParams; error: string }>
    ) => {
      state.isNewUserRegistering = false;
    },

    // Update Billing Admins List
    updateBillingAdminsListRequest: (
      state,
      action: PayloadAction<UpdateBillingAdminsListParams>
    ) => {
      state.isBillingAdminsListUpdating = true;
    },
    updateBillingAdminsListSuccess: (state) => {
      state.isBillingAdminsListUpdating = false;
    },
    updateBillingAdminsListFailure: (
      state,
      action: PayloadAction<{
        params: UpdateBillingAdminsListParams;
        error: string;
      }>
    ) => {
      state.isBillingAdminsListUpdating = false;
    },

    // Clear
    clear: () => initialState
  }
});

export const {
  getOrganizationRequest,
  getOrganizationSuccess,
  getOrganizationFailure,
  getOrganizationsRequest,
  getOrganizationsSuccess,
  getOrganizationsFailure,
  createOrganizationRequest,
  createOrganizationSuccess,
  createOrganizationFailure,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateOrganizationFailure,
  deleteOrganizationRequest,
  deleteOrganizationSuccess,
  deleteOrganizationFailure,
  leaveOrganizationRequest,
  leaveOrganizationSuccess,
  leaveOrganizationFailure,
  changeOrganizationOwnerRequest,
  changeOrganizationOwnerSuccess,
  changeOrganizationOwnerFailure,
  getGroupRequest,
  getGroupSuccess,
  getGroupFailure,
  getGroupsRequest,
  getGroupsSuccess,
  getGroupsFailure,
  createGroupRequest,
  createGroupSuccess,
  createGroupFailure,
  updateGroupRequest,
  updateGroupSuccess,
  updateGroupFailure,
  deleteGroupRequest,
  deleteGroupSuccess,
  deleteGroupFailure,
  leaveGroupRequest,
  leaveGroupSuccess,
  leaveGroupFailure,
  getOrganizationUsersRequest,
  getOrganizationUsersSuccess,
  getOrganizationUsersFailure,
  getGroupUsersRequest,
  getGroupUsersSuccess,
  getGroupUsersFailure,
  removeUserFromOrganizationRequest,
  removeUserFromOrganizationSuccess,
  removeUserFromOrganizationFailure,
  removeUserFromGroupRequest,
  removeUserFromGroupSuccess,
  removeUserFromGroupFailure,
  submitHubSpotFormRequest,
  submitHubSpotFormSuccess,
  submitHubSpotFormFailure,
  getOrganizationInvitationsRequest,
  getOrganizationInvitationsSuccess,
  getOrganizationInvitationsFailure,
  getOrganizationInvitationRequest,
  getOrganizationInvitationSuccess,
  getOrganizationInvitationFailure,
  inviteUserToOrganizationRequest,
  inviteUserToOrganizationSuccess,
  inviteUserToOrganizationFailure,
  acceptOrganizationInvitationRequest,
  acceptOrganizationInvitationSuccess,
  acceptOrganizationInvitationFailure,
  deleteOrganizationInvitationRequest,
  deleteOrganizationInvitationSuccess,
  deleteOrganizationInvitationFailure,
  getGroupInvitationsRequest,
  getGroupInvitationsSuccess,
  getGroupInvitationsFailure,
  getGroupInvitationRequest,
  getGroupInvitationSuccess,
  getGroupInvitationFailure,
  inviteUserToGroupRequest,
  inviteUserToGroupSuccess,
  inviteUserToGroupFailure,
  acceptGroupInvitationRequest,
  acceptGroupInvitationSuccess,
  acceptGroupInvitationFailure,
  deleteGroupInvitationRequest,
  deleteGroupInvitationSuccess,
  deleteGroupInvitationFailure,
  registerNewUserRequest,
  registerNewUserSuccess,
  registerNewUserFailure,
  updateBillingAdminsListRequest,
  updateBillingAdminsListSuccess,
  updateBillingAdminsListFailure,
  clear
} = enterprisesSlice.actions;

export const { actions: enterprisesActions } = enterprisesSlice;

export const reducer = enterprisesSlice.reducer;
